import React from 'react';

import { useQuery } from '@apollo/client/react';
import { c, msgid, ngettext } from 'ttag';

import { Alert, Button, Checkbox } from '@freelancelabs/components';

import { ListSkeleton } from '../../../components/Skeletons';
import { AssignableDashboardsDocument, PortalModulesDocument } from '../../../graphql/__generated__/types';
import { dashboardEmptiness } from './catalogue';
import { Draft, DraftDashboard } from './draft';

interface Props {
    draft: Draft;
    onChange: (patch: Partial<Draft>) => void;
}

/**
 * Step 3: the dashboards, an **optional** attribution.
 *
 * Attention, built empty-first, and it stays right: the `dashboard` catalogue had no writer
 * at all until PCG-79 (no mutation, no ingestion, no screen) so this step showed
 * its empty state everywhere. Entries can now be declared from the account, but an
 * account nobody has declared anything on still arrives here empty, which will be the
 * common case for a long while. The screen is therefore still arranged so that the
 * empty state is the one that reads well.
 *
 * **Where this parts ways with the mockup.** Step 4 of the mockup is a free-text
 * `<input type="url">` for a Metabase link, tested with a ping. That predates the
 * model and cannot be built: the catalogue is a **whitelist**, declaring an entry
 * *is* the authorisation, so a field where an operator pastes any URL would grant
 * a dashboard nobody vetted, and the embed token could not be locked to a
 * perimeter. The mockup is also singular where `InviteUserInput.dashboardIds` is
 * plural. The catalogue picker below is the model's shape, not the mockup's; the
 * mockup's wording is kept wherever it still describes the same fact.
 *
 * Entries are **grouped by account and never by a picker of accounts**: a dashboard
 * is the object of an account, and the journey has already chosen its accounts one
 * step earlier.
 */
const DashboardsStep = ({ draft, onChange }: Props) => {
    const organizationIds = draft.attachments.map((attachment) => attachment.organizationId);

    /**
     * Narrowed to the accounts step 2 retained, and that argument is not an
     * optimisation. A dashboard belonging to an account outside the selection is
     * refused by `inviteUser` at the send, once the role and the perimeter have been
     * composed: offering it here would let the operator build a draft that only
     * fails at the last screen.
     */
    const { data, loading, error, refetch } = useQuery(AssignableDashboardsDocument, {
        variables: { organizationIds },
    });

    const dashboards = (data?.assignableDashboards?.dashboards ?? []).filter(Boolean);
    const emptiness = dashboardEmptiness(error, data?.assignableDashboards?.emptyReason);

    /**
     * **Le nom du module vient du référentiel, il ne s'écrit pas ici.** La phrase
     * d'introduction annonçait « le module « Dashboard » du portail » : en anglais,
     * sur un écran français, pour un module que `portal_module` nomme « Tableau de
     * bord » et que le menu du portail affiche sous ce nom. L'opérateur lisait donc
     * un nom qui n'existe nulle part ailleurs, et un renommage du référentiel aurait
     * laissé la phrase mentir sans que rien n'échoue.
     *
     * C'est la règle de PCG-99, et la requête existe pour ça : son propre
     * commentaire dit « so that neither is written a second time in the front ».
     *
     * Attention, le code, lui, EST en dur, et c'est normal : `dashboard` est l'identifiant
     * stable du module que cette étape alimente, pas son libellé. C'est le libellé
     * qui dérive.
     */
    const { data: referential } = useQuery(PortalModulesDocument);
    const moduleLabel = (referential?.portalModules ?? []).find((module) => module?.code === 'dashboard')?.label;

    const selectedIds = new Set(draft.dashboards.map((dashboard) => dashboard.id));

    const toggle = (dashboard: DraftDashboard, checked: boolean) =>
        onChange({
            dashboards: checked
                ? [...draft.dashboards, dashboard]
                : draft.dashboards.filter((entry) => entry.id !== dashboard.id),
        });

    /**
     * The entries, grouped by their account and kept in the order the API sent them
     *: it already orders by account name then dashboard name, and re-sorting here
     * would be a second opinion on the same question.
     *
     * Grouped on the **id**, never on the name: two distinct accounts genuinely share
     * a name in this base: the account tree of step 2 shows two « Groupe Verlaine »,
     * so grouping by name would merge two companies under one heading and hand React
     * two identical keys. The name is only what the heading prints.
     */
    const groups = dashboards.reduce<
        { organizationId: string; organizationName: string; entries: typeof dashboards }[]
    >((acc, dashboard) => {
        const organizationId = dashboard.organizationId as string;
        const last = acc[acc.length - 1];
        if (last && last.organizationId === organizationId) {
            last.entries.push(dashboard);
            return acc;
        }
        return [
            ...acc,
            {
                organizationId,
                organizationName: dashboard.organizationName ?? c('Onboarding dashboards').t`Account without a name`,
                entries: [dashboard],
            },
        ];
    }, []);

    const count = draft.dashboards.length;

    return (
        <div className="flex flex-col gap-4">
            <div>
                <h2 className="text-sm font-semibold text-text-900">{c('Onboarding dashboards')
                    .t`Dashboards to grant`}</h2>
                {/* Deux phrases et non une avec un trou : le référentiel peut être en
                    vol, et « le module «  » du portail » se lirait comme un défaut.
                    La variante sans nom reste vraie, elle est simplement moins
                    précise, ce qui est le bon sens dans lequel se tromper. */}
                <p className="mt-1 text-xs text-text-600">
                    {moduleLabel
                        ? c('Onboarding dashboards')
                              .t`These feed the ${moduleLabel} module of the portal. Optional step: without a dashboard the module stays hidden for this person, which is a perfectly ordinary way to be onboarded.`
                        : c('Onboarding dashboards')
                              .t`These feed the dashboard module of the portal. Optional step: without a dashboard the module stays hidden for this person, which is a perfectly ordinary way to be onboarded.`}
                </p>
            </div>

            {loading && <ListSkeleton rows={4} withControl />}

            {/* Why there is nothing to tick, said as a fact about the catalogue rather
                than as an incident. An empty list rendered as a bare blank is the
                defect this whole step is arranged around. */}
            {!loading && emptiness && (
                <Alert type={emptiness.tone} title={emptiness.title}>
                    <p className="text-sm">{emptiness.guidance}</p>
                    {emptiness.detail && (
                        <p className="mt-2 break-words font-mono text-xs text-text-600">{emptiness.detail}</p>
                    )}
                    {/* Only where retrying is genuinely the fix. An empty catalogue
                        answers a second click with the same emptiness.

                        Wrapped in a block: the alert lays its children out in a column,
                        so a bare button stretches edge to edge and reads as the alert's
                        primary action rather than as the small way out that it is. */}
                    {error && (
                        <div className="mt-2">
                            <Button category="weak" size="small" onClick={() => refetch()}>
                                {c('Action').t`Retry`}
                            </Button>
                        </div>
                    )}
                </Alert>
            )}

            {groups.map((group) => (
                <section key={group.organizationId} className="rounded-lg border border-border-norm p-4">
                    <h3 className="text-sm font-semibold text-text-900">{group.organizationName}</h3>
                    <ul className="mt-3 flex flex-col gap-2">
                        {group.entries.map((dashboard) => {
                            const id = dashboard.id as string;
                            /**
                             * `perimeterLocked` false means the entry declares no locked
                             * parameter to carry the perimeter, so the token maker will
                             * refuse to sign and the habilitation would never open
                             * anything. Greyed with its reason rather than hidden: same
                             * reading as an account closed to the portal: hiding it would
                             * make the catalogue look emptier than it is, and send the
                             * operator declaring a dashboard that already exists.
                             */
                            const locked = dashboard.perimeterLocked === true;
                            const reasonId = locked ? undefined : `dashboard-reason-${id}`;
                            return (
                                <li key={id}>
                                    <Checkbox
                                        id={`dashboard-${id}`}
                                        checked={selectedIds.has(id)}
                                        disabled={!locked}
                                        aria-describedby={reasonId}
                                        onChange={(event) =>
                                            toggle(
                                                { id, organizationId: dashboard.organizationId as string },
                                                event.target.checked
                                            )
                                        }
                                    >
                                        <span className="text-sm">
                                            <b>{dashboard.name}</b>
                                            {!locked && (
                                                <span id={reasonId} className="ml-2 text-xs text-warning-600">
                                                    {c('Onboarding dashboards')
                                                        .t`Cannot be granted: this entry declares no locked perimeter parameter, so the access would be signed for nobody and open nothing. It has to be corrected where the catalogue is declared.`}
                                                </span>
                                            )}
                                        </span>
                                    </Checkbox>
                                </li>
                            );
                        })}
                    </ul>
                </section>
            ))}

            {!loading && !emptiness && (
                <p className="text-xs text-text-600">
                    {ngettext(msgid`${count} dashboard selected.`, `${count} dashboards selected.`, count)}
                </p>
            )}
        </div>
    );
};

export default DashboardsStep;
